import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import * as yup from 'yup';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input, PageHeader } from '../ui';
import { transactionService } from '../../services/transactionService';
// import { toast } from 'react-toastify'; // Add this import
import { toast } from 'react-hot-toast';

// Styled components for profile update
const ProfileContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 700,
  margin: '0 auto',
  padding: theme.spacing(4),
  backgroundColor: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    maxWidth: 320,
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: 420,
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 700,
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '50%',
    width: '50%',
  },
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
}));


const FormSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
}));

// Validation for amount
const amountSchema = yup.object({
  transaction_amount: yup
    .string()
    .required('Transaction amount is required')
});

type InitiatePaymentRequest = { transaction_amount: string };

export const TestForm: React.FC = () => {
  const { user } = useAuth();
  const [form, setForm] = useState<InitiatePaymentRequest>({ transaction_amount: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    try {
      await amountSchema.validate(form, { abortEarly: false });
    } catch (e: any) {
      setError(e?.errors?.[0] || 'Invalid amount');
      return;
    }

    setSubmitting(true);
    try {
      const res = await transactionService.initiatePayment(form.transaction_amount);
      setResult(res);
      if (res?.success) {
        toast.success(res.message || 'Payment Simulate Successfully');
        setTimeout(() => {
          if (res.payment_link) {
            window.open(res.payment_link, '_blank', 'noopener,noreferrer');
          }
        }, 3000);
      } else {
        setError(res?.message || 'Failed to Simulate payment');
        toast.error(res?.message || 'Failed to Simulate payment');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to Simulate payment');
      toast.error(e?.message || 'Failed to Simulate payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <PageHeader
        title="Simulate"
        showDivider={false}
      />
      <ProfileContainer>
      <ProfileHeader>
        <Typography
          variant="h4"
          weight="bold"
          color="textPrimary"
          sx={{ marginBottom: 1 }}
        >
          Simulate Payment
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter a transaction amount and submit
        </Typography>
      </ProfileHeader>

      <FormSection>
        <Input
                  size="small"
          label="Transaction Amount"
          type="number"
          placeholder="e.g. 100.00"
          fullWidth
          value={form.transaction_amount}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm({ transaction_amount: e.target.value })
          }

          inputProps={{ min: "0.01", max: "999999.99", step: "0.01" }}
        />

        <ButtonGroup>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !form.transaction_amount.trim()}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </ButtonGroup>
      </FormSection>
    </ProfileContainer>
    </>
  );
};

export default TestForm;